import React, { useCallback } from "react";
import { AlertLineReviewerRef } from "../../models/alert-line-reviewer/alert-line-reviewer-ref";
import { Button, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography, useTheme } from "@mui/material";
import { ReviewersHeaderContainer, ReviewerTable } from "./styles";
import { CustomDate } from "../../models/custom-date/custom-date";
import { intlFormatDateShort } from "../../utils/time-utils";
import AddEditAlertlineURLReviewer from "./AddEditAlertlineURLReviewer";
import { AlertLineURLDTO } from "../../models/alert-line-url/alert-line-url-dto";
import { useTranslation } from "react-i18next";
import EditDeletePopover from "../../reusable-components/Table/EditDeletePopover/EditDeletePopover";
import RemoveItem from "../../reusable-components/Table/RemoveItem/RemoveItem";
import { DTOModel } from "../../models/base/dto-model";
import { AlertLineReviewerApiService } from "../../services/alert-line/alert-line-reviewer-api.service";

interface Props {
  reviewers: AlertLineReviewerRef[];
  role?: "asHR" | "asManager" | "asCompanyCommitteeMember";
  handleUpdateParent: (reviewer: AlertLineReviewerRef, removed?: boolean) => void;
  alertLineURL: AlertLineURLDTO;
}
const AlertLineURLReviewer = ({ reviewers, role, handleUpdateParent, alertLineURL }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.add_edit_alert_line_url.alert_line_url_reviewer_table";
  const tRPFields = "components.add_edit_alert_line_url.add_edit_alert_line_url_reviewer.fields";
  const theme = useTheme();

  return (
    <>
      <ReviewerTable size="small">
        <TableHead>
          <TableRow>
            <TableCell align="center">Authorized as </TableCell>
            <TableCell align="center">{`${t(`${tRPFields}.user`)}`}</TableCell>
            <TableCell align="center">{`${t(`${tRPFields}.from_date`)}`}</TableCell>
            <TableCell align="center">{`${t(`${tRPFields}.to_date`)}`}</TableCell>
            <TableCell align="center"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell
              colSpan={5}
              style={{ backgroundColor: theme.palette.background.paper }}
            >
              <Grid style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Grid style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                  <Typography style={{ color: theme.palette.secondary.light }}>HR</Typography>
                  {reviewers.filter((reviewer) => reviewer.asHR).length === 0 && (
                    <Typography variant="body2">
                      <i> ({`${t(`${tRP}.no_reviewers`)}`})</i>
                    </Typography>
                  )}
                </Grid>
                <AddEditAlertlineURLReviewer
                  effect="add"
                  role={"asHR"}
                  handleUpdateParent={handleUpdateParent}
                  alertLineURL={alertLineURL}
                />
              </Grid>
            </TableCell>
          </TableRow>
          {reviewers
            .filter((reviewer) => reviewer.asHR)
            .map((reviewer) => (
              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  <Typography>{reviewer.user?.name}</Typography>
                  <Typography
                    variant="body2"
                    color={theme.palette.secondary.light}
                  >
                    {reviewer.viewOnly && `(${t(`${tRP}.view_only`)})`}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {reviewer.fromDate && intlFormatDateShort(CustomDate.ToDate(reviewer.fromDate)!)}
                </TableCell>
                <TableCell align="center">
                  {reviewer.toDate && intlFormatDateShort(CustomDate.ToDate(reviewer.toDate)!)}
                </TableCell>
                <TableCell>
                  <EditDeletePopover
                    EditElement={
                      <AddEditAlertlineURLReviewer
                        effect="edit"
                        handleUpdateParent={handleUpdateParent}
                        alertLineURL={alertLineURL}
                        item={reviewer}
                      />
                    }
                    DeleteElement={
                      <Button
                        onClick={() => handleUpdateParent(reviewer, true)}
                        style={{ color: theme.palette.error.main, textTransform: "none" }}
                      >
                        {`${t(`common_words.delete`)}`}
                      </Button>
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          <TableRow>
            <TableCell
              colSpan={5}
              style={{ backgroundColor: theme.palette.background.paper }}
            >
              <Grid style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Grid style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                  <Typography style={{ color: theme.palette.secondary.light }}>Manager</Typography>
                  {reviewers.filter((reviewer) => reviewer.asManager).length === 0 && (
                    <Typography variant="body2">
                      <i> ({`${t(`${tRP}.no_reviewers`)}`})</i>
                    </Typography>
                  )}
                </Grid>
                <AddEditAlertlineURLReviewer
                  effect="add"
                  role={"asManager"}
                  handleUpdateParent={handleUpdateParent}
                  alertLineURL={alertLineURL}
                />
              </Grid>
            </TableCell>
          </TableRow>
          {reviewers
            .filter((reviewer) => reviewer.asManager)
            .map((reviewer) => (
              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  <Typography>{reviewer.user?.name}</Typography>
                  <Typography
                    variant="body2"
                    color={theme.palette.secondary.light}
                  >
                    {reviewer.viewOnly && `(${t(`${tRP}.view_only`)})`}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {reviewer.fromDate && intlFormatDateShort(CustomDate.ToDate(reviewer.fromDate)!)}
                </TableCell>
                <TableCell align="center">
                  {reviewer.toDate && intlFormatDateShort(CustomDate.ToDate(reviewer.toDate)!)}
                </TableCell>
                <TableCell>
                  <EditDeletePopover
                    EditElement={
                      <AddEditAlertlineURLReviewer
                        effect="edit"
                        handleUpdateParent={handleUpdateParent}
                        alertLineURL={alertLineURL}
                        item={reviewer}
                      />
                    }
                    DeleteElement={
                      <Button
                        onClick={() => handleUpdateParent(reviewer, true)}
                        style={{ color: theme.palette.error.main }}
                      >
                        {`${t(`common_words.delete`)}`}
                      </Button>
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
          <TableRow>
            <TableCell
              colSpan={5}
              style={{ backgroundColor: theme.palette.background.paper }}
            >
              <Grid style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                <Grid style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
                  <Typography style={{ color: theme.palette.secondary.light }}>Company committee member</Typography>
                  {reviewers.filter((reviewer) => reviewer.asCompanyCommitteeMember).length === 0 && (
                    <Typography
                      variant="body2"
                      style={{ color: theme.palette.secondary.light }}
                    >
                      <i> ({`${t(`${tRP}.no_reviewers`)}`})</i>
                    </Typography>
                  )}
                </Grid>
                <AddEditAlertlineURLReviewer
                  effect="add"
                  role={"asCompanyCommitteeMember"}
                  handleUpdateParent={handleUpdateParent}
                  alertLineURL={alertLineURL}
                />
              </Grid>
            </TableCell>
          </TableRow>
          {reviewers
            .filter((reviewer) => reviewer.asCompanyCommitteeMember)
            .map((reviewer) => (
              <TableRow>
                <TableCell></TableCell>
                <TableCell>
                  <Typography>{reviewer.user?.name}</Typography>
                  <Typography
                    variant="body2"
                    color={theme.palette.secondary.light}
                  >
                    {reviewer.viewOnly && `(${t(`${tRP}.view_only`)})`}
                  </Typography>
                </TableCell>
                <TableCell align="center">
                  {reviewer.fromDate && intlFormatDateShort(CustomDate.ToDate(reviewer.fromDate)!)}
                </TableCell>
                <TableCell align="center">
                  {reviewer.toDate && intlFormatDateShort(CustomDate.ToDate(reviewer.toDate)!)}
                </TableCell>
                <TableCell>
                  <EditDeletePopover
                    EditElement={
                      <AddEditAlertlineURLReviewer
                        effect="edit"
                        handleUpdateParent={handleUpdateParent}
                        alertLineURL={alertLineURL}
                        item={reviewer}
                      />
                    }
                    DeleteElement={
                      <Button
                        onClick={() => handleUpdateParent(reviewer, true)}
                        style={{ color: theme.palette.error.main }}
                      >
                        {`${t(`common_words.delete`)}`}
                      </Button>
                    }
                  />
                </TableCell>
              </TableRow>
            ))}
        </TableBody>
      </ReviewerTable>
    </>
  );
};
export default AlertLineURLReviewer;
